import { useEffect, useRef } from "react";
import { Alert, Box, CircularProgress, Stack } from "@mui/material";
import {
  hlsSiblingUrl,
  nativeHlsSupported,
  streamPreviewVerdict,
  type StreamPreviewRefusal,
} from "@shared/stream-preview";
import type {
  SignagePlaylistPreview,
  SignagePreviewResource,
} from "@shared/types/signage";

/**
 * The player body behind `SignageChannelPreviewDialog` and the item-level
 * preview in `SignagePlaylistDialog`, pulled out once both needed it.
 *
 * ## Why the player is borrowed, not written twice
 *
 * The rules about what a browser may open live once, in `streamPreviewVerdict`,
 * and they are not obvious: an ENCRYPTED stream is unopenable here BY DESIGN
 * (production mints a Verimatrix licence per playback request and the CMS is
 * not a licence client), and DASH is a deliberate gap because it needs a
 * second player library to show a rendition that almost always has an HLS
 * sibling carrying the same content. A second copy of this logic is how the
 * two callers eventually disagree about what "cannot preview" means.
 *
 * ⚠️ A green preview here is NOT a green light for a television. Same caveat
 * as everywhere else this player is used - see the `panel-video-encoding`
 * note.
 */

const REFUSAL_TEXT: Record<StreamPreviewRefusal, string> = {
  no_url: "This clip has no stream URL, so there is nothing to open.",
  encrypted:
    "DRM-protected, so it cannot play in a browser. Production mints a Verimatrix licence per playback request and the CMS is not a licence client. The clip is fine — this preview simply is not a television.",
  protocol_unknown:
    "The catalogue does not say what this stream is, so nothing can select it — not this preview, and not a television either.",
  no_dash_player:
    "This clip is DASH, and there is no HLS rendition to preview instead. It plays on a wall; it will not open here.",
};

/**
 * Which rendition to try, which is NOT the one a wall would choose.
 *
 * A wall prefers DASH (`signagePlayableResource`, the platform's decision). A
 * browser can only play HLS here, so the preview prefers the HLS sibling of
 * the same title.
 */
function preferredForBrowser(
  resources: readonly SignagePreviewResource[]
): SignagePreviewResource | null {
  return (
    resources.find((r) => r.protocol === "hls" && !r.isEncrypted) ??
    resources.find((r) => r.protocol === "hls") ??
    resources.find((r) => r.protocol === "dash") ??
    resources[0] ??
    null
  );
}

export default function SignagePreviewPlayer({
  preview,
  loading,
  error,
  emptyText,
}: {
  preview: SignagePlaylistPreview | null;
  loading: boolean;
  error: string | null;
  /** What "nothing to play" means for the caller - a channel differs from a
   *  single row. */
  emptyText: string;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const detachRef = useRef<(() => void) | null>(null);

  /*
   * Tearing the player down matters more than it looks: an HLS instance left
   * attached keeps pulling segments off a CDN for a dialog nobody is looking
   * at.
   */
  useEffect(() => {
    return () => {
      detachRef.current?.();
      detachRef.current = null;
    };
  }, []);

  /*
   * Attach a player once the <video> exists AND we know what to play.
   *
   * `hls.js` is imported dynamically, so the CMS bundle every operator
   * downloads to edit a premise does not carry a video player for a dialog
   * most of them never open.
   */
  useEffect(() => {
    if (!preview) return;
    if (preview.kind !== "stream" && preview.kind !== "file") return;
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;

    if (preview.kind === "file") {
      video.src = preview.url;
      return () => {
        video.removeAttribute("src");
        video.load();
      };
    }

    const chosen = preferredForBrowser(preview.resources);
    if (!chosen) return;
    const verdict = streamPreviewVerdict(chosen, nativeHlsSupported(video));
    if (!verdict.playable) return;

    if (verdict.via === "native") {
      video.src = chosen.url;
      return () => {
        video.removeAttribute("src");
        video.load();
      };
    }

    void (async () => {
      const { default: Hls } = await import("hls.js");
      if (cancelled) return;
      // `enableWorker: false` - Foyer's CSP has no `worker-src`, so hls.js's
      // blob: worker is blocked. It survives that, but by way of an exception
      // on every load which is indistinguishable in the console from the real
      // errors this component exists to surface.
      const hls = new Hls({ enableWorker: false });
      hls.attachMedia(video);
      hls.loadSource(chosen.url);
      detachRef.current = () => hls.destroy();
    })();

    return () => {
      cancelled = true;
      detachRef.current?.();
      detachRef.current = null;
    };
  }, [preview]);

  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!preview) return null;

  if (preview.kind === "empty") {
    return <Alert severity="info">{emptyText}</Alert>;
  }
  if (preview.kind === "unresolvable") {
    return (
      <Alert severity="warning">
        The catalogue does not have this clip any more. A wall skips it rather
        than showing black — but somebody should look at why it is gone.
      </Alert>
    );
  }

  if (preview.kind === "stream") {
    const chosen = preferredForBrowser(preview.resources);
    /*
     * `false` for native HLS, and it does not matter here.
     *
     * There is no <video> to probe until this function decides to render one,
     * and the two HLS answers differ only in `via` - both are playable. The
     * refusal branch below cannot be changed by the browser fact, so assuming
     * MSE keeps the ordering honest instead of inventing a probe.
     */
    const verdict = chosen ? streamPreviewVerdict(chosen, false) : null;
    if (!chosen || !verdict?.playable) {
      const reason = chosen
        ? verdict?.playable === false
          ? verdict.reason
          : "no_url"
        : "no_url";
      const sibling = hlsSiblingUrl(preview.resources);
      return (
        <Alert severity="info">
          {REFUSAL_TEXT[reason]}
          {reason === "no_dash_player" && sibling ? (
            <Box sx={{ mt: 1, fontSize: 12, wordBreak: "break-all" }}>
              An HLS sibling may exist at {sibling}
            </Box>
          ) : null}
        </Alert>
      );
    }
  }

  return (
    <Box
      sx={{
        bgcolor: "#000",
        borderRadius: "10px",
        overflow: "hidden",
        display: "grid",
        placeItems: "center",
      }}
    >
      <video
        ref={videoRef}
        controls
        autoPlay
        playsInline
        style={{ width: "100%", maxHeight: "72vh", display: "block" }}
      />
    </Box>
  );
}
